/** Only approved static control names; no raw labels, input values, query strings or document/response data. */
export function installProductUsage(options: { endpoint: () => string; headers?: () => Record<string,string>; enabled?: () => boolean }) {
  if (typeof window === "undefined" || (window as any).__wtUsageInstalled) return () => {};
  (window as any).__wtUsageInstalled = true;
  const nativeFetch = window.fetch.bind(window), listeners: (() => void)[] = [];
  const uuid = () => window.crypto.randomUUID ? window.crypto.randomUUID() : "10000000-1000-4000-8000-100000000000".replace(/[018]/g,c => (Number(c)^window.crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> Number(c)/4).toString(16));
  const sessionStorageKey="wt.productUsage.session.v1";
  let sessionId=uuid(),sequence=0;
  try {
    const saved=JSON.parse(sessionStorage.getItem(sessionStorageKey)||"null");
    if(saved&&/^[0-9a-f-]{36}$/i.test(saved.id)&&Number.isSafeInteger(saved.sequence)&&saved.sequence>=0&&Date.now()-saved.lastAt>=0&&Date.now()-saved.lastAt<1800000){sessionId=saved.id;sequence=saved.sequence;}
  } catch { /* Storage may be disabled; telemetry remains optional. */ }
  function checkpoint(){try{sessionStorage.setItem(sessionStorageKey,JSON.stringify({id:sessionId,sequence,lastAt:Date.now()}));}catch{/* Never interrupt the application. */}}
  checkpoint();
  let attemptId = uuid(), buffer: any[] = [], busy = false, stopped = false, failures = 0, nextSend = 0, lastActive = 0, clientDropped=0, pageObserved=false;
  const safeSegments = new Set("app utvalg leave timesheet candidate assignment recruitment education experience courses skills contacts contracts salary absence dashboard medarbeider kandidat kandidater oppdrag rekruttering timeliste timelister faktura fakturering kunde kunder signering cv profil meldinger documents dokumenter settings oppsett home jobbgruppe jobbgrupper stillinger stilling postkasse tilgjengelighet login auth search get save create update delete approve submit send sign api profile me privacy timesheets detail draft invoices presentations preview candidates calendar department tilskudd consent innleie tilbud nytt ny rediger grupper statistikk admin edit availability messages jobs applications assignments registration payslips benefits".split(" "));
  const controlNames: Record<string,string> = {
    "save": "save",
    "lagre": "save",
    "save and close": "save-close",
    "lagre og lukk": "save-close",
    "create": "create",
    "opprett": "create",
    "new": "new",
    "ny": "new",
    "nytt": "new",
    "edit": "edit",
    "rediger": "edit",
    "delete": "delete",
    "slett": "delete",
    "cancel": "cancel",
    "avbryt": "cancel",
    "close": "close",
    "lukk": "close",
    "next": "next",
    "neste": "next",
    "back": "back",
    "tilbake": "back",
    "confirm": "confirm",
    "bekreft": "confirm",
    "approve": "approve",
    "godkjenn": "approve",
    "reject": "reject",
    "avvis": "reject",
    "send": "send",
    "search": "search",
    "s\u00f8k": "search",
    "filter": "filter",
    "filtrer": "filter",
    "add": "add",
    "legg til": "add",
    "remove": "remove",
    "fjern": "remove",
    "sign": "sign",
    "signer": "sign",
    "download": "download",
    "last ned": "download",
    "upload": "upload",
    "last opp": "upload",
    "submit": "submit",
    "send inn": "submit",
    "continue": "continue",
    "fortsett": "continue",
    "finish": "finish",
    "fullf\u00f8r": "finish",
    "open": "open",
    "\u00e5pne": "open",
    "copy": "copy",
    "kopier": "copy",
    "refresh": "refresh",
    "oppdater": "refresh",
    "publish": "publish",
    "publiser": "publish",
    "preview": "preview",
    "forh\u00e5ndsvis": "preview",
    "select all": "select-all",
    "velg alle": "select-all",
    "s\u00f8k p\u00e5 stilling": "apply-job",
    "registrer timer": "register-hours",
    "legg til timer": "add-hours",
    "godkjenn timer": "approve-hours",
    "godkjenn timeliste": "approve-timesheet",
    "send timeliste": "submit-timesheet",
    "send til godkjenning": "submit-approval",
    "lagre statusendringer": "save-candidate-status",
    "forh\u00e5ndsvis annonse": "preview-advert",
    "opprett oppdrag": "create-assignment",
    "nytt oppdrag": "create-assignment",
    "opprett rekruttering": "create-recruitment",
    "legg til kandidat": "add-candidate",
    "send melding": "send-message",
    "ny melding": "new-message",
    "svar": "reply",
    "forkast": "discard",
    "endre passord": "change-password",
    "last opp cv": "upload-cv",
    "til stillingsoversikten": "jobs-list"
  };
  const controls = new Set(Object.values(controlNames));
  function controlAction(target:Element) {
    const explicit=target.getAttribute("data-wt-usage-action");
    if(explicit&&controls.has(explicit))return explicit;
    // Compare only against approved static labels. Never send raw text, arbitrary DOM IDs or input values.
    for(const label of [target.getAttribute("aria-label"),target.getAttribute("title"),target.textContent]) {
      const approved=controlNames[(label||"").trim().toLowerCase().replace(/\s+/g," ")];
      if(typeof approved==="string")return approved;
    }
    return null;
  }
  const safePath = (pathname:string) => pathname.split("/").filter(Boolean).map(s => safeSegments.has(s.toLowerCase()) ? s.toLowerCase() : ":id").join("/").slice(0,118);
  const path = () => safePath(new URL(location.href).pathname);
  const moduleName = () => new URL(location.href).pathname.split("/").map(s=>s.toLowerCase()).find(s=>safeSegments.has(s)&&s!=="app") || "other";
  const emit = (category:string, action:string, durationMs?:number, eventAttempt=attemptId, eventRoute?:string) => {
    if(stopped || (options.enabled && !options.enabled()))return;
    if(category==="navigation"&&action==="page.view")pageObserved=true;
    if(buffer.length>=200){buffer.shift();clientDropped++;}
    buffer.push({eventId:uuid(),sessionId,attemptId:eventAttempt,sequence:++sequence,occurredAtUtc:new Date().toISOString(),category,action,module:moduleName(),route:eventRoute||"/"+path(),durationMs,clientDropped,_headers:options.headers?options.headers():{}});
  };
  async function flush() {
    if(busy || stopped || buffer.length===0 || Date.now()<nextSend)return;
    if(options.enabled&&!options.enabled()){buffer=[];return;}
    checkpoint();busy=true;const context=JSON.stringify(buffer[0]._headers),count=buffer.findIndex((e,i)=>i>=50||JSON.stringify(e._headers)!==context);
    const batch=buffer.splice(0,count<0?Math.min(50,buffer.length):count);
    const controller=new AbortController(),timeout=window.setTimeout(()=>controller.abort(),5000);
    try {
      const response=await nativeFetch(options.endpoint(),{method:"POST",credentials:"same-origin",headers:{"Content-Type":"application/json","X-Wt-Usage":"1",...batch[0]._headers},body:JSON.stringify(batch.map(({_headers,...e})=>e)),signal:controller.signal});
      if(response.status===202){const receipt=await response.json();clientDropped+=Math.max(0,Number(receipt.dropped)||0);failures=0;nextSend=0;}
      else if(response.status===400 || response.status===401 || response.status===403){clientDropped+=batch.length;nextSend=Date.now()+60000;}
      else throw new Error("Unavailable");
    }catch{
      failures++;if(failures<=3){const pending=[...batch,...buffer];clientDropped+=Math.max(0,pending.length-200);buffer=pending.slice(0,200);}else clientDropped+=batch.length;
      nextSend=Date.now()+Math.min(60000,5000*Math.pow(2,Math.min(failures,4)));
    }finally{clearTimeout(timeout);busy=false;}
  }
  function on(target:EventTarget,name:string,fn:EventListener,capture=false){target.addEventListener(name,fn,{capture,passive:true});listeners.push(()=>target.removeEventListener(name,fn,capture));}
  function active(){if(Date.now()-lastActive>60000){lastActive=Date.now();emit("activity","activity.active");}}
  on(document,"click",((event:MouseEvent)=>{
    if(!event.isTrusted)return;active();const target=event.target instanceof Element?event.target.closest("button,a,input[type=submit],input[type=button],[role=button],[role=menuitem]"):null;
    if(!target)return;const name=controlAction(target);emit("interaction",(target.tagName==="A"?"link.click":"button.click")+(name?"."+name:""));
  }) as EventListener,true);
  const forms=new WeakSet<Element>();
  on(document,"focusin",((event:FocusEvent)=>{if(!event.isTrusted)return;active();const form=event.target instanceof Element?event.target.closest("form"):null;if(form&&!forms.has(form)){forms.add(form);attemptId=uuid();emit("form","form.started");}}) as EventListener,true);
  on(document,"submit",(()=>emit("form","form.submitted")) as EventListener,true);
  on(document,"invalid",(()=>emit("form","form.invalid")) as EventListener,true);
  on(document,"keydown",(()=>active()) as EventListener,true);
  on(window,"error",((event:Event)=>{if(event instanceof ErrorEvent)emit("error","client.error");}) as EventListener);
  on(window,"unhandledrejection",(()=>emit("error","client.unhandled-rejection")) as EventListener);
  const originalPush=history.pushState, originalReplace=history.replaceState;
  let lastPath=location.pathname;const navigation=()=>{const next=location.pathname;if(next!==lastPath){lastPath=next;emit("navigation","page.view");}};
  history.pushState=function(...args:Parameters<History["pushState"]>){originalPush.apply(history,args);navigation();};
  history.replaceState=function(...args:Parameters<History["replaceState"]>){originalReplace.apply(history,args);navigation();};
  on(window,"popstate",navigation);
  window.fetch=(async(input:RequestInfo|URL,init?:RequestInit)=>{
    const url=new URL(input instanceof Request?input.url:String(input),location.href);
    const method=(init?.method || (input instanceof Request?input.method:"GET")).toUpperCase();
    const track=url.origin===location.origin&&method!=="GET"&&method!=="HEAD"&&!/presence|usage|insights|mcp|poll|logapi|auth|token|\/cvp\/(?:cvp)?(?:get|search|find|list|check|has|is|load|read)|\/get|\/search|\/find|\/list|\/check/i.test(url.pathname);
    const observeRead=url.origin===location.origin&&!/presence|usage|insights|mcp|poll|logapi|auth|token/i.test(url.pathname);
    if(stopped||(options.enabled&&!options.enabled())||(!track&&!observeRead))return nativeFetch(input,init);
    if(!track){
      // Read requests keep their original headers; only failures enter the existing telemetry queue.
      const started=performance.now(),readAttempt=attemptId;
      try{const response=await nativeFetch(input,init);if(response.status>=400)emit("operation","request.failed.http-"+response.status,Math.min(3600000,Math.round(performance.now()-started)),readAttempt,"/"+safePath(url.pathname));return response;}
      catch(error){emit("operation","request.failed.network",Math.min(3600000,Math.round(performance.now()-started)),readAttempt,"/"+safePath(url.pathname));throw error;}
    }
    const headers=new Headers(input instanceof Request?input.headers:undefined);new Headers(init?.headers).forEach((v,k)=>headers.set(k,v));
    headers.set("X-Wt-Usage-Session",sessionId);headers.set("X-Wt-Usage-Attempt",attemptId);emit("operation","operation.attempted");
    const currentAttempt=attemptId,start=performance.now(),sample=Math.random()<.1;
    try {const response=await nativeFetch(input,{...init,headers});const failed=response.status>=400||response.headers.get("X-Wt-Usage-Outcome")==="failure";if(failed||sample)emit("operation",failed?(response.status>=400?"operation.failed.http-"+response.status:"operation.failed.business"):"operation.response",Math.min(3600000,Math.round(performance.now()-start)),currentAttempt);if(response.headers.get("X-Wt-Usage-Outcome")==="success"&&attemptId===currentAttempt)attemptId=uuid();return response;}
    catch(error){emit("operation","operation.failed.network",Math.min(3600000,Math.round(performance.now()-start)),currentAttempt);throw error;}
  }) as typeof window.fetch;
  on(document,"visibilitychange",()=>{checkpoint();if(document.visibilityState==="hidden"&&!busy&&buffer.length){
    // Custom headers required by some portals cannot be supplied by sendBeacon; fetch still handles normal batches.
    if(!options.headers&&navigator.sendBeacon){const batch=buffer.slice(0,50);if(navigator.sendBeacon(options.endpoint(),new Blob([JSON.stringify(batch.map(({_headers,...e})=>e))],{type:"application/json"})))buffer.splice(0,batch.length);}
    else void flush();
  }});
  const timer=window.setInterval(()=>{if(!pageObserved)emit("navigation","page.view");void flush();},5000);emit("navigation","page.view");
  return ()=>{checkpoint();stopped=true;clearInterval(timer);listeners.forEach(f=>f());window.fetch=nativeFetch;history.pushState=originalPush;history.replaceState=originalReplace;(window as any).__wtUsageInstalled=false;};
}
